// ExpectedToday.jsx — Predicted-arrival panel for the cashier/manager.
// Lets the manager pick ANY date (past, today, or future) and shows:
//   - Past dates: hit/miss accuracy of the prediction model
//   - Today:      arrived / expected_now / overdue (actionable lists)
//   - Future:     expected list only
import React, { useState, useEffect, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { toast } from "sonner";
import { fm } from "./lib/analytics";
import { todayISO as todayIso, daysAheadISO, daysAgoISO } from "./lib/dates";
import { downloadCSV } from "./lib/export";
import BulkWhatsAppModal from "./BulkWhatsAppModal";
import DateField from "./components/DateField";
import { toggleRowPhone, toggleAllShown as toggleAllShownPure, selectedFrom, countSelectedIn, emptySelection } from "./lib/tableSelection";
import { InfoTip } from "./components/InfoTip";
import { useCampaignSendsLock, CAMPAIGN_SENDS_LOCKED_MSG } from "./hooks/useCampaignSendsLock";

function exportList(rows, name) {
  if (!rows || rows.length === 0) return;
  const header = ["#", "الاسم", "الجوال", "الزيارات", "متوسط الفجوة (يوم)", "إجمالي الإنفاق (ر)", "آخر زيارة", "تنبؤ القادم", "اليوم المعتاد", "أكثر ساعة", "59 X", "59"];
  const rowsCsv = rows.map((r, i) => [
    i + 1, r.name || "", r.phone || "", r.visits, r.avg_gap_days,
    r.total_spent, r.last_visit, r.predicted_next,
    r.predicted_weekday || "", r.top_hour_label || "",
    r.is59x ? "نعم" : "", r.is59 ? "نعم" : "",
  ]);
  downloadCSV(name, header, rowsCsv);
}

const shiftIso = (days) => (days >= 0 ? daysAheadISO(days) : daysAgoISO(-days));

const CONF_META = {
  "عالية": { bg: "#dcfce7", fg: "#15803d", bd: "#15803d" },
  "متوسطة": { bg: "#fef9c3", fg: "#a16207", bd: "#ca8a04" },
  "منخفضة": { bg: "#fee2e2", fg: "#b91c1c", bd: "#b91c1c" },
};
function ConfBadge({ grade }) {
  const m = CONF_META[grade] || CONF_META["منخفضة"];
  return (
    <span data-testid={`conf-badge-${grade}`} style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: m.bg, color: m.fg, border: `1px solid ${m.bd}`, whiteSpace: "nowrap" }}>
      {grade || "منخفضة"}
    </span>
  );
}

function CustomerTable({ rows, type, emptyMsg, testId, onContactOne, confFilter, selectable, selectedPhones, onToggleRow, onToggleAllShown, sendsLocked }) {
  const shown = (confFilter && confFilter !== "الكل")
    ? (rows || []).filter((r) => (r.confidence || "منخفضة") === confFilter)
    : (rows || []);
  if (shown.length === 0) {
    return <div data-testid={`${testId}-empty`} style={{ padding: 18, textAlign: "center", color: "var(--muted)", fontSize: 12.5 }}>{(confFilter && confFilter !== "الكل") ? `لا عملاء بدرجة «${confFilter}»` : emptyMsg}</div>;
  }
  const shownPhones = shown.map((r) => r.phone).filter(Boolean);
  const allShownSelected = selectable && shownPhones.length > 0 && shownPhones.every((p) => selectedPhones?.has(p));
  return (
    <div className="tw" data-testid={testId}>
      <table>
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 34 }}>
                <input type="checkbox" checked={!!allShownSelected}
                  onChange={() => onToggleAllShown && onToggleAllShown(shown)}
                  data-testid={`${testId}-select-all`} />
                <InfoTip text="تحديد المعروض حاليًا" />
              </th>
            )}
            <th>#</th>
            <th>الاسم</th>
            <th>الجوال</th>
            <th>الزيارات</th>
            <th>متوسط الفجوة</th>
            <th>الإنفاق</th>
            <th>{type === "overdue" ? "فاتهم موعدهم" : "وقت معتاد"}</th>
            <th>الثقة</th>
            <th>تواصل</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={r.phone + i} data-testid={`${testId}-row-${i}`}>
              {selectable && (
                <td>
                  <input type="checkbox"
                    checked={!!selectedPhones?.has(r.phone)}
                    onChange={() => onToggleRow && onToggleRow(r.phone)}
                    disabled={!r.phone}
                    data-testid={`${testId}-check-${r.phone}`} />
                </td>
              )}
              <td>{i + 1}</td>
              <td>
                <b style={{ color: "var(--brown-deep)" }}>{r.name}</b>
                {r.is59x && <span style={{ marginInlineStart: 4, fontSize: 10, color: "var(--warn)" }}>🚫 59X</span>}
                {r.is59 && <span style={{ marginInlineStart: 4, fontSize: 10, color: "var(--success)" }}>✅ 59</span>}
              </td>
              <td style={{ direction: "ltr", fontFamily: "monospace" }}>{r.phone}</td>
              <td>{fm(r.visits)}</td>
              <td>{r.avg_gap_days} يوم</td>
              <td style={{ color: "var(--success)", fontWeight: 600 }}>{fm(Math.round(r.total_spent))} ر</td>
              <td>
                {type === "overdue" ? (
                  <span style={{ color: r.delta_days >= 5 ? "var(--danger)" : "var(--warn)", fontWeight: 700 }}>
                    {r.delta_days} يوم
                  </span>
                ) : (
                  <span>
                    {r.predicted_weekday}{r.top_hour_label && ` · ${r.top_hour_label}`}
                  </span>
                )}
              </td>
              <td data-testid={`conf-cell-${r.phone}`}><ConfBadge grade={r.confidence} /></td>
              <td>
                {r.phone ? (
                  <button type="button" className="btn btn-success"
                    onClick={() => onContactOne && onContactOne(r, type === "overdue" ? "overdue" : type === "arrived" ? "arrived" : "expected")}
                    disabled={sendsLocked}
                    style={{ fontSize: 11, padding: "4px 8px", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}
                    data-testid={`contact-one-${r.phone}`}>
                    📩 قالب
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: 12, borderInlineStart: `4px solid ${color}` }}>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Quick-pick date shortcuts (relative to today)
const DATE_PRESETS = [
  { id: "y2", label: "قبل يومين", offset: -2 },
  { id: "y1", label: "أمس", offset: -1 },
  { id: "t0", label: "اليوم", offset: 0 },
  { id: "t1", label: "غداً", offset: 1 },
  { id: "t7", label: "بعد أسبوع", offset: 7 },
];

export default function ExpectedToday() {
  // جولة تصحيح 2026-10-14 (المرحلة 1) / البند 12 — مفتاح إيقاف الإرسال الجماعي
  const { locked: sendsLocked } = useCampaignSendsLock();
  const [target, setTarget] = useState(todayIso());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState({ expected: false, overdue: false, arrived: false, correct: false, missed: false });

  // v119.147 — Phase 8 bulk WhatsApp modal
  const [bulkWa, setBulkWa] = useState(null); // { customers: [], category: "expected|overdue|arrived" }
  // (أ) البند 3 — نسب الموثوقية لكل درجة تُقرأ من tenant_settings (لا مصلَّبة).
  const [reliability, setReliability] = useState(null);
  // ⑤/L4 — نافذة «فاتهم موعدهم» من الإعدادات (لا 2-7 ثابتة في الواجهة).
  const [missedWin, setMissedWin] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/customers/expected-model-settings")
      .then((r) => {
        if (cancelled) return;
        const st = r.data?.settings || {};
        setReliability(st.reliability_pct || null);
        if (st.missed_window_min != null) setMissedWin([st.missed_window_min, st.missed_window_max]);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // جولة تصحيح 2026-10-14 (المرحلة 2، البند 10) — الدقّة المقاسة
  // الفعلية من prediction_log (ما تحقّق فعلاً)، تحلّ محلّ سطر «الموثوقية»
  // الثابت أعلاه الذي كان رقماً مُفترَضاً من الإعدادات لا مقيساً من
  // نتائج فعلية. نفس مصدر بطاقة PredictionAccuracyCard في مركز العملاء
  // الموحّد — محرك واحد، لا قراءتان متباينتان لمفهوم «الدقّة».
  const [realAccuracy, setRealAccuracy] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/customer-metrics/prediction-accuracy", { params: { window_days: 30 } })
      .then((r) => { if (!cancelled) setRealAccuracy(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // v119.153 — segmented sub-view (fights clutter, one section at a time)
  const [view, setView] = useState("expected"); // expected | arrived | overdue
  const [confFilter, setConfFilter] = useState("الكل"); // البند 5 — فلتر درجة الثقة
  const [branch, setBranch] = useState("all"); // البند الفروع — تفصيل الفروع
  // (ب) البنود 4-9 — التحديد اليدوي من الجدول الرئيسي (مفتاح = رقم الجوال)
  const [selectedPhones, setSelectedPhones] = useState(() => emptySelection());
  const clearSelection = useCallback(() => setSelectedPhones(emptySelection()), []);
  // البند 9 — يُمسح التحديد عند تغيير التاريخ/الفلتر/الفرع/التبويب
  useEffect(() => { setSelectedPhones(emptySelection()); }, [target, confFilter, branch, view]);
  const toggleRow = useCallback((phone) => {
    setSelectedPhones((s) => toggleRowPhone(s, phone));
  }, []);
  // البند 5 — «تحديد الكل» يشمل المعروض حاليًا فقط (يحترم فلتر الدرجة المطبّق)
  const toggleAllShown = useCallback((shownRows) => {
    setSelectedPhones((s) => toggleAllShownPure(s, shownRows));
  }, []);

  const load = useCallback(async (dateIso, branchId = "all") => {
    setBusy(true); setErr(null);
    try {
      const { data: d } = await api.get(
        `/customers/expected-today?target=${encodeURIComponent(dateIso)}&branch=${encodeURIComponent(branchId)}`
      );
      setData(d);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
      setData(null);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(target, branch); }, [target, branch, load]);

  const limit = (k) => showAll[k] ? Infinity : 30;
  const toggle = useCallback((k) => setShowAll((s) => ({ ...s, [k]: !s[k] })), []);
  // Part C · 3a — per-row contact opens the approved-template modal for ONE
  // customer (no free-text wa.me path remains in this screen).
  const contactOne = useCallback((c, category) => setBulkWa({ customers: [c], category }), []);
  // (ب) البند 7 — فتح المودال بالمحددين يدويًا من قائمة الفئة الحالية
  const sendSelected = useCallback((list, category) => {
    const picked = selectedFrom(list, selectedPhones);
    if (picked.length === 0) { toast.error("لم تُحدّد أي عميل من الجدول"); return; }
    setBulkWa({ customers: picked, category, manual: picked });
  }, [selectedPhones]);
  // عدد المحددين ضمن قائمة فئة معيّنة (للعدّاد فوق الجدول)
  const selectedCountIn = useCallback(
    (list) => countSelectedIn(list, selectedPhones),
    [selectedPhones]);

  const isPast = data?.is_past;
  const isFuture = data?.is_future;
  const accuracyData = data?.accuracy;

  return (
    <div data-testid="expected-today-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header + date picker */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div>
            <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>🔮 الزوار المتوقعون</h3>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
              تنبؤ بناءً على متوسط الفجوة بين زيارات كل عميل (≥3 زيارات سابقة، نستبعد العملاء غير المنتظمين). اختر أي تاريخ — للماضي يعرض دقة النموذج، للحاضر/المستقبل قائمة المتوقعين.
              <br />
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                <b>✅ تنبؤ صحيح</b> = توقعنا حضوره وفعلاً جاء في نفس اليوم (±يوم). <b>❌ تنبؤ خاطئ</b> = توقعناه ولم يأتِ. <b>👤 إجمالي الواصلين</b> = كل من جاء فعلياً اليوم.
              </span>
            </p>
            {data?.source && (
              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <span data-testid="expected-source-badge" style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 999,
                  background: data.source === "live" ? "#dcfce7" : "#fef3c7",
                  color:      data.source === "live" ? "#15803d" : "#92400e",
                  fontWeight: 700,
                }}>
                  {data.source === "live" ? "🟢 مباشر من RETM" : "📄 من رفع CSV"}
                </span>
                {/* v121.1 — Model transparency badge */}
                {data?.model && (
                  <span
                    data-testid="expected-model-badge"
                    style={{
                      fontSize: 10, padding: "2px 8px", borderRadius: 999,
                      background: data.model === "v4" ? "#e0e7ff" : "#f3e8ff",
                      color:      data.model === "v4" ? "#4338ca" : "#6b21a8",
                      fontWeight: 700, cursor: "help",
                    }}
                  ><InfoTip text={`النموذج النشط: ${data.model.toUpperCase()} — بطاقة «دقة التنبؤ» في مركز العملاء`} />
                    🎯 نموذج {data.model.toUpperCase()}
                  </span>
                )}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-soft" onClick={() => load(target, branch)} disabled={busy} data-testid="reload-expected">
            {busy ? "⏳ تحميل..." : "🔄 تحديث"}
          </button>
        </div>

        {/* Phase 7h — فلتر مستقل لتبويب «الزوار المتوقعون»: نمط تاريخ مفرد
            بنفس لغة تصميم CustomerFilterBar (صندوق أبيض بحدّ تركوازي + وسم
            صغير تركوازي). لا نطاق — التوقّع ليوم واحد. */}
        <section
          data-testid="expected-filter-bar"
          dir="rtl"
          style={{
            marginTop: 6, padding: "14px 18px", borderRadius: 16,
            background: "#fff", border: "1px solid rgba(6,95,70,0.20)",
            boxShadow: "0 1px 3px rgba(6,95,70,0.06)",
            display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
            fontVariantNumeric: "lining-nums tabular-nums",
          }}
        >
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>🔗 فلتر التبويب — تاريخ مفرد</span>
            <DateField
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="target-date-input"
              style={{
                fontSize: 13, padding: "6px 10px", borderRadius: 10,
                border: "1px solid rgba(6,95,70,0.20)", background: "#fff",
                color: "#065F46", fontWeight: 600, width: 170,
              }}
            />
          </label>
          <div style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>اختصارات سريعة</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`btn btn-soft ${target === shiftIso(p.offset) ? "active" : ""}`}
                  onClick={() => setTarget(shiftIso(p.offset))}
                  data-testid={`preset-${p.id}`}
                  style={{ fontSize: 11, padding: "6px 12px", borderRadius: 10 }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 10, color: "#0F766E", fontWeight: 700 }}>🏬 الفرع</span>
            <select
              data-testid="branch-filter-select"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              style={{
                fontSize: 13, padding: "6px 10px", borderRadius: 10,
                border: "1px solid rgba(6,95,70,0.20)", background: "#fff",
                color: "#065F46", fontWeight: 600, minWidth: 160,
              }}
            >
              <option value="all">كل الفروع (موحّد)</option>
              {(data?.branch?.available || []).map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div data-testid="expected-window-label"
               style={{ marginInlineStart: "auto", fontSize: 11, color: "#0F766E", fontWeight: 600 }}>
            التاريخ المختار: {target || "—"}
          </div>
        </section>
      </div>

      {/* البند الفروع — تنبيه مرئي عند الرجوع التلقائي للموحّد (2a) */}
      {data?.branch?.fallback && (
        <div className="al alo" data-testid="branch-fallback-alert"
             style={{ marginBottom: 12, fontSize: 12.5, fontWeight: 600 }}>
          ⚠ {data.branch.fallback_reason}
        </div>
      )}
      {/* البند الفروع — عدد فواتير فراغ الفرع المستبعدة في وضع الفروع (غير صامت) */}
      {data?.branch?.active && data.branch.excluded_empty_branch > 0 && (
        <div className="al" data-testid="branch-excluded-note"
             style={{ marginBottom: 12, fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "8px 12px" }}>
          ℹ️ في وضع الفرع «{data.branch.available.find((b) => b.id === data.branch.active)?.name || data.branch.active}»: استُبعدت{" "}
          <b>{data.branch.excluded_empty_branch}</b> فاتورة بلا فرع محدّد (لا تُنسب لأي فرع).
          {data.branch.br_test_excluded > 0 && (
            <> كما استُبعد <b>{data.branch.br_test_excluded}</b> سجل تجريبي (BR-TEST) من كل الأوضاع.</>
          )}
        </div>
      )}

      {err && <div className="al alr" data-testid="expected-error">⚠ {err}</div>}

      {data && (
        <>
          {/* Past-date Accuracy Banner */}
          {isPast && accuracyData && (
            <div className="card" style={{ marginBottom: 14, background: "linear-gradient(135deg, var(--beige) 0%, #fff 100%)", borderInlineStart: "5px solid var(--info)" }} data-testid="accuracy-banner">
              <h4 style={{ margin: "0 0 8px", color: "var(--brown-deep)" }}>📊 دقة النموذج في يوم {data.today} ({data.weekday})</h4>
              <div className="g4" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                <div style={{ padding: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: accuracyData.accuracy_pct >= 50 ? "var(--success)" : "var(--warn)" }} data-testid="accuracy-pct">
                    {accuracyData.accuracy_pct}%
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>دقة التنبؤ</div>
                </div>
                <div style={{ padding: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{fm(accuracyData.predicted)}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>عدد المتوقعين</div>
                </div>
                <div style={{ padding: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--success)" }}>{fm(accuracyData.correct)}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>تنبؤات صحيحة</div>
                </div>
                <div style={{ padding: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--danger)" }}>{fm(accuracyData.missed)}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>تنبؤات فشلت</div>
                </div>
                <div style={{ padding: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{fm(accuracyData.actual_arrived)}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>إجمالي الواصلين الفعلي</div>
                </div>
                <div style={{ padding: 8, textAlign: "center", background: "rgba(124,58,237,0.06)", borderRadius: 8 }} data-testid="accuracy-surprise">
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#7C3AED" }}>{fm(Math.max(0, (accuracyData.actual_arrived || 0) - (accuracyData.correct || 0)))}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>🎁 مفاجآت سعيدة</div>
                  <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 1 }}>جاؤوا بدون توقع</div>
                </div>
              </div>
              <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(140,95,50,0.05)", borderRadius: 6, fontSize: 11, color: "var(--muted)", lineHeight: 1.7 }}>
                💡 <b>الفرق بين &quot;تنبؤات صحيحة&quot; و &quot;إجمالي الواصلين&quot;:</b> الجدول الأخضر تحت يعرض كل من جاء ذلك اليوم ({fm(accuracyData.actual_arrived)} عميل)، أما عداد &quot;تنبؤات صحيحة&quot; ({fm(accuracyData.correct)}) فيعدّ فقط من توقعنا حضورهم تحديداً. الباقي ({fm(Math.max(0, (accuracyData.actual_arrived || 0) - (accuracyData.correct || 0)))}) جاؤوا بدون توقعنا.
              </div>
            </div>
          )}

          {/* Active context strip — events + weather that adjust the count */}
          {(data.context_multiplier && data.context_multiplier !== 1) || (data.context?.events?.length) || data.context?.weather ? (
            <div data-testid="ct-context-strip" style={{
              display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 12,
              padding: "8px 12px", background: "#fff", borderRadius: 6,
              border: "1px solid #BAE6FD",
            }}>
              <span style={{ fontWeight: 700, color: "#0E7490", fontSize: 12 }}>سياقات نشطة:</span>
              {(data.context?.events || []).map((ev, i) => (
                <span key={`ev${i}`} style={{
                  padding: "2px 8px", borderRadius: 10, background: "#FEF3C7",
                  color: "#92400E", fontWeight: 700, fontSize: 11,
 }} ><InfoTip text={`المصدر: ${ev.source}`} />
                  📅 {ev.name} ×{ev.multiplier}
                </span>
              ))}
              {data.context?.weather && (
                <span style={{
                  padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                  background: data.context.weather.bucket === "hot" ? "#FEE2E2"
                           : data.context.weather.bucket === "cold" ? "#DBEAFE" : "#FEF3C7",
                  color:      data.context.weather.bucket === "hot" ? "#DC2626"
                           : data.context.weather.bucket === "cold" ? "#0891B2" : "#92400E",
                }}>
                  {data.context.weather.bucket === "hot" ? "🔥" : data.context.weather.bucket === "cold" ? "❄" : "🌤"} {data.context.weather.temp_max_c}°
                  {data.context.weather.multiplier !== 1 && ` ×${data.context.weather.multiplier}`}
                </span>
              )}
              <span style={{ marginInlineStart: "auto", fontSize: 11, color: "#0E7490", fontWeight: 700 }}>
                المضاعف النهائي: ×{data.context_multiplier}
              </span>
            </div>
          ) : null}

          {/* Summary KPIs */}
          <div className="g4" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14 }}>
            <Kpi color="var(--info)" label={`📆 ${isPast ? "ماضٍ" : isFuture ? "مستقبل" : "اليوم"} — ${data.weekday}`} value={data.today} sub="تاريخ التحليل" />
            <Kpi color="#7C3AED" label="🎯 إجمالي التوقع" value={fm(data.totals.predicted_total || (data.totals.arrived + data.totals.expected_now))} sub={isPast ? "= صحيح + خاطئ" : "= وصلوا + قادمون"} />
            {data.totals.predicted_total_adjusted != null && data.context_multiplier !== 1 && (
              <Kpi
                color={data.context_multiplier > 1 ? "#16A34A" : "#D97706"}
                label="🧠 توقع مُعدَّل بالسياق"
                value={fm(data.totals.predicted_total_adjusted)}
                sub={`×${data.context_multiplier} (فعاليات + طقس)`}
              />
            )}
            <Kpi color="var(--success)" label={isPast ? "✅ تنبؤ صحيح" : "✅ وصلوا اليوم"} value={fm(isPast ? data.totals.predicted_correct : data.totals.arrived)} sub={isPast ? "توقعنا وحضروا" : "عملاء تم خدمتهم"} />
            <Kpi color="var(--warn)" label={isPast ? "❌ تنبؤ خاطئ" : "⏳ متوقعون"} value={fm(isPast ? data.totals.predicted_missed : data.totals.expected_now)} sub={isPast ? "توقعنا ولم يحضروا" : "استعدّ لاستقبالهم"} />
            {!isFuture && <Kpi color="var(--danger)" label="⚠ فاتهم موعدهم" value={fm(data.totals.overdue)} sub="تحتاج استرجاع" />}
          </div>

          {/* v119.153 — Segmented sub-tabs (anti-clutter) */}
          <div data-testid="expected-sub-tabs" style={{
            display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14,
            padding: 4, background: "var(--beige)", borderRadius: 10,
          }}>
            {[
              { id: "expected", label: `⏳ متوقعون (${fm(data.expected_now?.length || 0)})` },
              { id: "arrived",  label: `✅ وصلوا (${fm(data.arrived?.length || 0)})` },
              !isFuture && { id: "overdue", label: `⚠ فاتهم موعدهم (${fm(data.overdue?.length || 0)})` },
            ].filter(Boolean).map((t) => {
              const active = view === t.id;
              return (
                <button key={t.id} type="button"
                        data-testid={`sub-tab-${t.id}`}
                        onClick={() => setView(t.id)}
                        className="btn"
                        style={{
                          flex: "1 1 140px",
                          fontSize: 12, fontWeight: 600,
                          background: active ? "#fff" : "transparent",
                          color: active ? "var(--brown-deep)" : "var(--muted)",
                          border: active ? "1px solid #d4a574" : "1px solid transparent",
                          boxShadow: active ? "0 1px 3px rgba(74,44,26,0.08)" : "none",
                          padding: "6px 10px", borderRadius: 8, transition: "all 0.15s",
                        }}>
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* البند 5 — فلتر درجة الثقة (يطبَّق على كل الجداول) */}
          {data && (
            <div data-testid="conf-filter-bar" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "0 0 12px", padding: "8px 12px", background: "#faf6ee", border: "1px solid #eadfc8", borderRadius: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)" }}>🎯 تصفية بالثقة:</span>
              {["الكل", "عالية", "متوسطة", "منخفضة"].map((g) => {
                const active = confFilter === g;
                const m = g === "الكل" ? { bg: "#8b5a2b", fg: "#fff" } : CONF_META[g];
                return (
                  <button key={g} type="button" data-testid={`conf-filter-${g}`}
                    onClick={() => setConfFilter(g)}
                    style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 12px", borderRadius: 999, cursor: "pointer",
                      background: active ? (g === "الكل" ? "#8b5a2b" : m.bg) : "#fff",
                      color: active ? (g === "الكل" ? "#fff" : m.fg) : "#6b5535",
                      border: `1px solid ${active ? (g === "الكل" ? "#8b5a2b" : m.bd) : "#eadfc8"}` }}>
                    {g}
                  </button>
                );
              })}
              {realAccuracy && (
                <span data-testid="conf-filter-reliability" style={{ fontSize: 10.5, color: "#8a6d3b", marginInlineStart: "auto" }}>
                  {realAccuracy.enough_data
                    ? `📊 الدقّة المقاسة فعلياً (آخر ${realAccuracy.window_days} يوم): ` +
                      realAccuracy.levels.map((l) => `${l.confidence} ${l.accuracy_pct ?? "—"}%`).join(" · ")
                    : `📊 الدقّة المقاسة: لا تكفي التنبؤات المقيَّمة بعد (${realAccuracy.total_evaluated} من ${Math.max(realAccuracy.min_evaluated_to_display, realAccuracy.total_logged)})`}
                </span>
              )}
            </div>
          )}

          {/* Predicted-correct (past only, "arrived" view) */}
          {view === "arrived" && isPast && data.predicted_correct?.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="st" style={{ background: "var(--success)", color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span>✅ تنبؤات صحيحة — توقعنا حضورهم وفعلاً جاؤوا</span>
                <button type="button" className="btn btn-soft" data-testid="export-correct" onClick={() => exportList(data.predicted_correct, `تنبؤات_صحيحة_${data.today}`)} style={{ fontSize: 11, padding: "4px 10px" }}>
                  📥 تصدير ({fm(data.predicted_correct.length)})
                </button>
              </div>
              <CustomerTable rows={data.predicted_correct.slice(0, limit("correct"))} type="expected" emptyMsg="—" testId="correct-table" onContactOne={contactOne} confFilter={confFilter} sendsLocked={sendsLocked} />
              {data.predicted_correct.length > 30 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button type="button" className="btn btn-soft" onClick={() => toggle("correct")} data-testid="toggle-correct" style={{ fontSize: 12 }}>
                    {showAll.correct ? "🔼 إخفاء" : `🔽 عرض الكل (${fm(data.predicted_correct.length)})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Expected now (or predicted-but-missed for past) */}
          {view === "expected" && !isFuture && data.expected_now?.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="st" style={{ background: "var(--warn)", color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span>{isPast ? "❌ تنبؤات فشلت — توقعنا حضورهم ولم يأتوا" : "⏳ متوقعون اليوم — أرسل تذكيراً!"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {!isPast && (
                    <button type="button" className="btn btn-soft" data-testid="bulk-wa-expected"
                            onClick={() => setBulkWa({ customers: data.expected_now, category: "expected" })}
                            disabled={sendsLocked}
                            style={{ fontSize: 11, padding: "4px 10px", background: "#25D366", color: "#fff", border: "none", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}>
                      📩 بلّغ الكل عبر واتساب
                    </button>
                  )}
                  {!isPast && selectedCountIn(data.expected_now) > 0 && (
                    <button type="button" className="btn btn-soft" data-testid="bulk-wa-expected-selected"
                            onClick={() => sendSelected(data.expected_now, "expected")}
                            disabled={sendsLocked}
                            style={{ fontSize: 11, padding: "4px 10px", background: "#1e40af", color: "#fff", border: "none", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}>
                      📩 إرسال للمحدّدين ({fm(selectedCountIn(data.expected_now))})
                    </button>
                  )}
                  <button type="button" className="btn btn-soft" data-testid="export-expected" onClick={() => exportList(data.expected_now, `متوقعون_${data.today}`)} style={{ fontSize: 11, padding: "4px 10px" }}>
                    📥 تصدير ({fm(data.expected_now.length)})
                  </button>
                </div>
              </div>
              {!isPast && selectedCountIn(data.expected_now) > 0 && (
                <div data-testid="expected-selection-bar" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 12px", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: "#3730a3" }}>تم اختيار {fm(selectedCountIn(data.expected_now))} عميلًا</span>
                  <button type="button" className="btn btn-soft" data-testid="clear-selection-expected" onClick={clearSelection} style={{ fontSize: 11, padding: "3px 10px" }}>إلغاء التحديد</button>
                </div>
              )}
              <CustomerTable rows={data.expected_now.slice(0, limit("expected"))} type="expected" emptyMsg="—" testId="expected-now-table" onContactOne={contactOne} confFilter={confFilter}
                selectable={!isPast} selectedPhones={selectedPhones} onToggleRow={toggleRow} onToggleAllShown={toggleAllShown} sendsLocked={sendsLocked} />
              {data.expected_now.length > 30 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button type="button" className="btn btn-soft" onClick={() => toggle("expected")} data-testid="toggle-expected" style={{ fontSize: 12 }}>
                    {showAll.expected ? "🔼 إخفاء (عرض 30 فقط)" : `🔽 عرض الكل (${fm(data.expected_now.length)})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Future-date expected (full list, future ledger) */}
          {view === "expected" && isFuture && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="st" style={{ background: "var(--info)", color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span>🔮 متوقعون في {data.today} — خطّط لهم مسبقاً</span>
                <button type="button" className="btn btn-soft" data-testid="export-expected" onClick={() => exportList(data.expected_now, `متوقعون_${data.today}`)} disabled={!data.expected_now?.length} style={{ fontSize: 11, padding: "4px 10px" }}>
                  📥 تصدير ({fm(data.expected_now?.length || 0)})
                </button>
              </div>
              <CustomerTable rows={(data.expected_now || []).slice(0, limit("expected"))} type="expected" emptyMsg="لا توجد توقعات لهذا اليوم" testId="future-expected-table" onContactOne={contactOne} confFilter={confFilter} sendsLocked={sendsLocked} />
              {(data.expected_now?.length || 0) > 30 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button type="button" className="btn btn-soft" onClick={() => toggle("expected")} data-testid="toggle-expected" style={{ fontSize: 12 }}>
                    {showAll.expected ? "🔼 إخفاء (عرض 30 فقط)" : `🔽 عرض الكل (${fm(data.expected_now.length)})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Overdue — recovery (not for future) */}
          {view === "overdue" && !isFuture && data.overdue?.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="st" style={{ background: "var(--danger)", color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span data-testid="missed-window-title">⚠ فاتهم موعدهم — تجاوزوا موعدهم المعتاد{missedWin ? ` (${missedWin[0]}-${missedWin[1]} أيام)` : ""}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" className="btn btn-soft" data-testid="bulk-wa-overdue"
                          onClick={() => setBulkWa({ customers: data.overdue, category: "overdue" })}
                          disabled={sendsLocked}
                          style={{ fontSize: 11, padding: "4px 10px", background: "#25D366", color: "#fff", border: "none", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}>
                    📩 استرجاع بالجملة
                  </button>
                  {selectedCountIn(data.overdue) > 0 && (
                    <button type="button" className="btn btn-soft" data-testid="bulk-wa-overdue-selected"
                            onClick={() => sendSelected(data.overdue, "overdue")}
                            disabled={sendsLocked}
                            style={{ fontSize: 11, padding: "4px 10px", background: "#1e40af", color: "#fff", border: "none", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}>
                      📩 إرسال للمحدّدين ({fm(selectedCountIn(data.overdue))})
                    </button>
                  )}
                  <button type="button" className="btn btn-soft" data-testid="export-overdue" onClick={() => exportList(data.overdue, `فاتهم_موعدهم_${data.today}`)} style={{ fontSize: 11, padding: "4px 10px" }}>
                    📥 تصدير ({fm(data.overdue.length)})
                  </button>
                </div>
              </div>
              {selectedCountIn(data.overdue) > 0 && (
                <div data-testid="overdue-selection-bar" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 12px", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: "#3730a3" }}>تم اختيار {fm(selectedCountIn(data.overdue))} عميلًا</span>
                  <button type="button" className="btn btn-soft" data-testid="clear-selection-overdue" onClick={clearSelection} style={{ fontSize: 11, padding: "3px 10px" }}>إلغاء التحديد</button>
                </div>
              )}
              <CustomerTable rows={data.overdue.slice(0, limit("overdue"))} type="overdue" emptyMsg="✅ لا أحد فاته موعده" testId="overdue-table" onContactOne={contactOne} confFilter={confFilter}
                selectable selectedPhones={selectedPhones} onToggleRow={toggleRow} onToggleAllShown={toggleAllShown} sendsLocked={sendsLocked} />
              {data.overdue.length > 30 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button type="button" className="btn btn-soft" onClick={() => toggle("overdue")} data-testid="toggle-overdue" style={{ fontSize: 12 }}>
                    {showAll.overdue ? "🔼 إخفاء (عرض 30 فقط)" : `🔽 عرض الكل (${fm(data.overdue.length)})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Arrived list */}
          {view === "arrived" && data.arrived?.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="st" style={{ background: "var(--success)", color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span>{isPast ? `✅ وصلوا في ${data.today}` : "✅ وصلوا اليوم — اشكرهم"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {!isPast && (
                    <button type="button" className="btn btn-soft" data-testid="bulk-wa-arrived"
                            onClick={() => setBulkWa({ customers: data.arrived, category: "arrived" })}
                            disabled={sendsLocked}
                            style={{ fontSize: 11, padding: "4px 10px", background: "#25D366", color: "#fff", border: "none", opacity: sendsLocked ? 0.5 : 1, cursor: sendsLocked ? "not-allowed" : "pointer" }}>
                      📩 اشكر الكل عبر واتساب
                    </button>
                  )}
                  <button type="button" className="btn btn-soft" data-testid="export-arrived" onClick={() => exportList(data.arrived, `وصلوا_${data.today}`)} style={{ fontSize: 11, padding: "4px 10px" }}>
                    📥 تصدير ({fm(data.arrived.length)})
                  </button>
                </div>
              </div>
              <CustomerTable rows={data.arrived.slice(0, limit("arrived"))} type="arrived" emptyMsg="لم يصل أحد بعد" testId="arrived-table" onContactOne={contactOne} confFilter={confFilter} sendsLocked={sendsLocked} />
              {data.arrived.length > 30 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button type="button" className="btn btn-soft" onClick={() => toggle("arrived")} data-testid="toggle-arrived" style={{ fontSize: 12 }}>
                    {showAll.arrived ? "🔼 إخفاء (عرض 30 فقط)" : `🔽 عرض الكل (${fm(data.arrived.length)})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {bulkWa && (
        <BulkWhatsAppModal
          customers={bulkWa.customers}
          category={bulkWa.category}
          manualCustomers={bulkWa.manual || null}
          reliability={reliability}
          onClose={() => setBulkWa(null)}
        />
      )}

      <AutomationControl sendsLocked={sendsLocked} />
      <ModelSettingsPanel />
      <ScheduledBulkList />
    </div>
  );
}

// ─── v119.217 — One-switch daily automation control ─────────────────────────
function AutomationControl({ sendsLocked }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/customers/wa-automation").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    setBusy(true);
    try {
      const next = { ...cfg, ...patch };
      await api.put("/customers/wa-automation", next);
      setCfg(next);
      if (patch.enabled === true) toast.success("🤖 الأتمتة اليومية مفعّلة — سيجدول النظام الرسائل كل صباح تلقائياً");
      if (patch.enabled === false) toast("⏸ الأتمتة متوقفة");
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر الحفظ");
    }
    setBusy(false);
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/customers/wa-automation/run-now");
      toast.success(`🤖 جُدولت ${r.data.planned} رسالة الآن`, {
        description: (r.data.jobs || []).map((j) => `${j.send_at_ksa} → ${j.count}`).join(" · ") || undefined,
      });
      window.dispatchEvent(new CustomEvent("tw-bulk-wa-scheduled"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر التشغيل");
    }
    setBusy(false);
  };

  if (!cfg) return null;
  const on = !!cfg.enabled;
  const groups = cfg.groups || ["expected"];
  const toggleGroup = (g) => {
    const next = groups.includes(g) ? groups.filter((x) => x !== g) : [...groups, g];
    if (!next.length) { toast.error("اختر مجموعة واحدة على الأقل"); return; }
    saveCfg({ groups: next });
  };

  return (
    <div className="card" style={{ padding: 14, marginTop: 12, border: on ? "1.5px solid rgba(46,139,87,0.45)" : undefined }}
         data-testid="wa-automation-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)" }}>
            🤖 الأتمتة اليومية للرسائل
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, lineHeight: 1.7 }}>
            كل صباح (7:00) يجدول النظام تلقائياً رسالة لكل زائر متوقع <b>قبل ساعة من وقت زيارته المعتاد</b> — مع حماية التكرار.
            {cfg.last_planned_date && <> · آخر تخطيط: {cfg.last_planned_date} ({cfg.last_result?.planned ?? 0} رسالة)</>}
          </div>
          {sendsLocked && (
            <div data-testid="wa-automation-locked-note" style={{ fontSize: 11, color: "var(--danger, #b91c1c)", marginTop: 4, fontWeight: 700 }}>
              {CAMPAIGN_SENDS_LOCKED_MSG}
            </div>
          )}
        </div>
        {/* iOS master switch */}
        <button onClick={() => saveCfg({ enabled: !on })} disabled={busy || sendsLocked}
               
                data-testid="wa-automation-master-toggle" aria-checked={on} role="switch"
                style={{ width: 54, height: 30, borderRadius: 999, border: "none", cursor: sendsLocked ? "not-allowed" : "pointer",
                         background: on ? "#34C759" : "#e5e5ea", position: "relative", opacity: sendsLocked ? 0.5 : 1,
                         transition: "background .2s ease", flexShrink: 0 }}>
          <span style={{ position: "absolute", top: 3, insetInlineStart: on ? 27 : 3,
                          width: 24, height: 24, borderRadius: 999, background: "#fff",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.25)", transition: "inset-inline-start .2s ease" }} />
        </button>
      </div>
      {on && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>المجموعات:</span>
          {[["expected", "🔮 المتوقعون اليوم"], ["overdue", "⏳ فاتهم موعدهم (اشتقنا لك)"]].map(([g, label]) => (
            <button key={g} onClick={() => toggleGroup(g)} disabled={busy}
                    data-testid={`wa-automation-group-${g}`}
                    style={{ padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontSize: 11.5, fontWeight: 700,
                             fontFamily: "inherit",
                             border: groups.includes(g) ? "1.5px solid #34C759" : "1px solid var(--border)",
                             background: groups.includes(g) ? "rgba(52,199,89,0.1)" : "#fff",
                             color: groups.includes(g) ? "#1d7a3d" : "var(--muted)" }}>
              {groups.includes(g) ? "✓ " : ""}{label}
            </button>
          ))}
          <button onClick={runNow} disabled={busy || sendsLocked} data-testid="wa-automation-run-now"
                 
                  style={{ marginInlineStart: "auto", padding: "6px 14px", borderRadius: 10, cursor: sendsLocked ? "not-allowed" : "pointer", opacity: sendsLocked ? 0.5 : 1,
                           fontSize: 11.5, fontWeight: 800, fontFamily: "inherit", border: "none",
                           background: "var(--brown-deep, #4a2c1a)", color: "#fff" }}>
            {busy ? "⏳" : "⚡ شغّلها الآن لليوم"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── v122 · البند 2 — Expected-model thresholds editor (owner) ──────────────
const MODEL_FIELDS = [
  ["min_visits", "الحد الأدنى للزيارات (min_visits)", 1],
  ["conf_visits_high", "زيارات «عالية» (>)", 1],
  ["conf_visits_mid", "زيارات «متوسطة» (≥)", 1],
  ["cv_low", "تشتت منخفض لـ«عالية» (cv ≤)", 0.05],
  ["cv_high", "تشتت مرتفع → «منخفضة» (cv >)", 0.05],
  ["freshness_ratio", "بوابة الحداثة (× متوسط الفجوة)", 0.1],
  ["wd_conc", "تركيز يوم الأسبوع (wd_conc)", 0.05],
  ["avg_gap_min", "أدنى متوسط فجوة (>)", 1],
  ["avg_gap_max", "أقصى متوسط فجوة (≤)", 1],
];
function ModelSettingsPanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/customers/expected-model-settings")
      .then((r) => setS(r.data?.settings || null)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/customers/expected-model-settings", s);
      setS(r.data?.settings || s);
      toast.success("💾 حُفظت عتبات النموذج — تُطبَّق فورًا على التوقعات");
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر الحفظ (صلاحية المالك)");
    }
    setBusy(false);
  };
  if (!s) return null;
  return (
    <div className="card" style={{ padding: 14, marginTop: 12 }} data-testid="model-settings-panel">
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
        🎚️ عتبات نموذج الزوار المتوقعين
      </div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.7 }}>
        كل العتبات هنا (لا في الكود). التشتت لم يعد يُقصي أحدًا — يؤثّر في درجة الثقة فقط.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10 }}>
        {MODEL_FIELDS.map(([key, label, step]) => (
          <label key={key} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: "#5c3a1e" }}>
            <span style={{ fontWeight: 700 }}>{label}</span>
            <input type="number" step={step} value={s[key]} data-testid={`model-field-${key}`}
              onChange={(e) => setS({ ...s, [key]: e.target.value === "" ? "" : Number(e.target.value) })}
              style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #eadfc8", fontFamily: "inherit", fontSize: 13 }} />
          </label>
        ))}
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, color: "#5c3a1e" }}>
          <span style={{ fontWeight: 700 }}>أتمتة: أدنى درجة ثقة (البند 6)</span>
          <select value={s.automation_min_confidence} data-testid="model-field-automation_min_confidence"
            onChange={(e) => setS({ ...s, automation_min_confidence: e.target.value })}
            style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #eadfc8", fontFamily: "inherit", fontSize: 13 }}>
            {["عالية", "متوسطة", "منخفضة"].map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
      </div>
      <button onClick={save} disabled={busy} data-testid="model-settings-save"
        className="btn btn-primary" style={{ marginTop: 12, fontSize: 12.5 }}>
        {busy ? "⏳ حفظ…" : "💾 حفظ العتبات"}
      </button>
    </div>
  );
}

// ─── v119.211 — Pending scheduled bulk WA sends (with cancel) ───────────────
const SCHED_CAT_AR = { expected: "متوقعون", overdue: "فاتهم موعدهم", arrived: "وصلوا" };
function ScheduledBulkList() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/customers/whatsapp-bulk/scheduled")
      .then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("tw-bulk-wa-scheduled", load);
    return () => window.removeEventListener("tw-bulk-wa-scheduled", load);
  }, [load]);

  const cancel = async (id) => {
    if (!window.confirm("إلغاء هذه الرسالة المجدولة؟")) return;
    try {
      await api.delete(`/customers/whatsapp-bulk/scheduled/${id}`);
      toast.success("أُلغيت الجدولة");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر الإلغاء");
    }
  };

  if (!items.length) return null;
  return (
    <div className="card" style={{ padding: 14, marginTop: 12 }} data-testid="scheduled-bulk-list">
      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8 }}>
        ⏰ رسائل مجدولة ({items.length})
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {items.map((it) => (
          <div key={it.id} data-testid={`scheduled-bulk-${it.id}`}
               style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                        padding: "8px 12px", borderRadius: 10, border: "1px solid var(--border)",
                        background: "#fff9ec", fontSize: 12 }}>
            <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>
              📩 {SCHED_CAT_AR[it.category] || it.category} · {it.count} عميل
            </span>
            <span style={{ color: "var(--muted)" }} dir="ltr">
              🗓 {String(it.send_at_ksa || "").replace("T", " ")} KSA
            </span>
            {it.status === "sending" && <span style={{ color: "#D97706", fontWeight: 700 }}>⏳ يُرسل الآن…</span>}
            {it.status === "pending" && (
              <button onClick={() => cancel(it.id)} data-testid={`cancel-scheduled-${it.id}`}
                      style={{ marginInlineStart: "auto", background: "none", border: "1px solid #eadfc8",
                               borderRadius: 8, padding: "3px 10px", cursor: "pointer",
                               fontSize: 11, color: "#b9533e" }}>🗑 إلغاء</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
